<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { fetchDatasets } from '@/services/api';
import { useLayoutComponents } from '@/composables/layout.js';
import { useDatasets } from '@/composables/useDatasets';
import InfiniteScroll from "@/components/infinite-scroll.vue";

const { Card } = useLayoutComponents();
const router = useRouter();
const { createDatasetFileObject } = useDatasets();

const datasets = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const pageSize = ref(12);
const hasMoreItems = ref(true);

const processedDatasets = computed(() => {
  return datasets.value.map(dataset => ({
    dataset_id: dataset.id,
    dataset_title: dataset.name || '',
    dataset_description: dataset.description || '',
    dataset_file_name: dataset.file_name || '',
    dataset_file_size: dataset.file_size || '',
    dataset_file_extension: dataset.file_extension || '',
    dataset_file_schema: dataset.schema || [],
    seller_id: dataset.owner_user_id,
    file: createDatasetFileObject(dataset),
    status: dataset.status || '',
    delivery_method: 'Eval',
    price: dataset.price || '',
    currency: dataset.currency || '',
    image: dataset.image || '',
    eval_id: dataset.eval_id || '',
    eval_price: dataset.eval_price || null,
    external_source_id: dataset.external_source_id || ''
  }));
});

const getDatasetBadge = (dataset) => {
  if (dataset.eval_id) {
    return 'Eval';
  }
  if (dataset.external_source_id) {
    return 'External';
  }
  return 'Upload';
};

const fetchDatasetsPage = async (page) => {
  isLoading.value = true;
  try {
    if (page === 1) {
      currentPage.value = 1;
      datasets.value = [];
      hasMoreItems.value = true;
    }
    
    const skip = (page - 1) * pageSize.value;
    const datasetsData = await fetchDatasets(skip, pageSize.value, 'eval', undefined, true);
   
    if (page === 1) {
      datasets.value = datasetsData || [];
    } else {
      datasets.value = [...datasets.value, ...(datasetsData || [])];
    }
    
    hasMoreItems.value = (datasetsData || []).length >= pageSize.value;
        
  } catch (error) {
    console.error('Error fetching eval datasets:', error);
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  if (!hasMoreItems.value || isLoading.value) return;
  currentPage.value += 1;
  await fetchDatasetsPage(currentPage.value);
};

const handleAddNew = () => {
  router.push('/creator-profile?page=eval-builder&subpage=eval-builder');
};

const handleEditItem = (dataset) => {
  router.push({
    name: 'creator-profile',
    query: { 
      page: 'eval-builder',
      datasetId: dataset.dataset_id
    }
  });
};

onMounted(async () => {
  await fetchDatasetsPage(1);
});
</script>

<template>
  <InfiniteScroll 
    :load-more="loadMore" 
    :loading="isLoading" 
    :has-more="hasMoreItems"
    :has-items="processedDatasets.length > 0" 
    :scroll-container="'.creator-profile__container'"
  >
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
      <Card 
        :is-add-card="true" 
        @add="handleAddNew"
        dataTestId="new-eval-dataset-button"
      />
      
      <Card
        v-for="dataset in processedDatasets"
        :key="dataset.dataset_id"
        :title="dataset.dataset_title"
        :description="dataset.dataset_description"
        :deliveryMethod="getDatasetBadge(dataset)"
        :file="dataset.file"
        :schema="dataset.dataset_file_schema"
        :eval_price="dataset.eval_price || null"
        :show-edit="true"
        @edit="handleEditItem(dataset)"
      />
    </div>

    <template #loading>
      <div class="loading-spinner"></div>
    </template>

    <template #end>
      <div class="text-gray-500 dark:text-gray-400">
        No more items to load
      </div>
    </template>
  </InfiniteScroll>
</template>

<style scoped>
.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 4px solid rgba(43, 45, 51, 1);
  border-top-color: #FE3E01;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>